import { useCallback } from "react";
import { DialogHeader, DialogTitle } from "@repo/ui";
import { StatusSection } from "./status/status-section";
import { useListData } from "../hooks/use-list-data";
import type { ListDTO } from "../list.types";
import { useChangeWarning } from "./dialog-components/change-warning-dialog";
import { ListDialogFooter } from "./dialog-components/list-dialog-footer";
import { useListForm } from "./hooks/use-list-form";
import { useStatusSelection } from "../hooks/use-status-selection";
import { ListFormFields } from "./dialog-components/list-form-fields";
import { useListDialogHandlers } from "./hooks/use-list-dialog-handlers";
import { useListSubmission } from "./hooks/use-list-submission";

type Props = {
  mode: "create" | "edit";
  list?: ListDTO;
  onClose?: () => void;
  onSuccess?: () => void;
};

export const ListDialog = ({ mode, list, onClose, onSuccess }: Props) => {
  const {
    allWorkflows,
    allCustomers,
    workflowsLoading,
    customersLoading,
    createList,
    updateList,
    getWorkflowStatuses,
    getWorkflowListStatuses,
  } = useListData();

  const {
    formData,
    setFormData,
    customerOptions,
    workflowOptions,
    isValid,
    hasChanges: hasFormChanges,
  } = useListForm(mode, list, allWorkflows, allCustomers);

  const { data: workflowStatuses, isLoading: statusesLoading } = getWorkflowStatuses(
    formData.workflowId
  );

  const { data: workflowListStatuses, isLoading: listStatusesLoading } = getWorkflowListStatuses(
    formData.workflowId,
    list?.id ?? null
  );

  const {
    selectedCodes,
    setSelectedCodes,
    availableStatuses,
    chosenStatuses,
    hasStatusChanges,
    resetStatuses,
    reorderChosenStatuses,
    selectedAvailableItems,
    setSelectedAvailableItems,
    selectedSelectedItems,
    setSelectedSelectedItems,
  } = useStatusSelection(mode, workflowListStatuses, workflowStatuses);

  const { showChangeWarning: showWarningDialog } = useChangeWarning();

  const showChangeWarning = useCallback(
    (changeType: "customer" | "workflow", newValue: number | null) => {
      showWarningDialog({
        changeType,
        newValue,
        onConfirm: (type, value) => {
          if (type === "customer") {
            setFormData((prev) => ({
              ...prev,
              customerId: value,
              workflowId: null,
            }));
          } else {
            setFormData((prev) => ({ ...prev, workflowId: value }));
          }
          resetStatuses();
        },
      });
    },
    [showWarningDialog, setFormData, resetStatuses]
  );

  const { handleCustomerChange, handleWorkflowChange, handleNameChange } = useListDialogHandlers({
    formData,
    setFormData,
    selectedStatuses: selectedCodes,
    resetStatuses,
    showChangeWarning,
  });

  const { isSubmitting, handleSubmit: submitList } = useListSubmission({
    mode,
    list,
    createList,
    updateList,
    onSuccess,
    onClose,
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.name || !formData.workflowId) {
        return;
      }

      submitList(
        { name: formData.name, workflowId: formData.workflowId },
        chosenStatuses,
        workflowListStatuses ?? []
      );
    },
    [submitList, formData, chosenStatuses, workflowListStatuses]
  );

  const hasChanges = hasFormChanges || hasStatusChanges;

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>{mode === "create" ? "Create New List" : "Edit List"}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-6">
        <ListFormFields
          mode={mode}
          formData={formData}
          customerOptions={customerOptions}
          workflowOptions={workflowOptions}
          customersLoading={customersLoading}
          workflowsLoading={workflowsLoading}
          onCustomerChange={handleCustomerChange}
          onWorkflowChange={handleWorkflowChange}
          onNameChange={handleNameChange}
        />
        <StatusSection
          setSelectedAvailableItems={setSelectedAvailableItems}
          setSelectedSelectedItems={setSelectedSelectedItems}
          setSelectedCodes={setSelectedCodes}
          selectedAvailableItems={selectedAvailableItems}
          selectedSelectedItems={selectedSelectedItems}
          availableStatuses={availableStatuses}
          chosenStatuses={chosenStatuses}
          formData={formData}
          statusesLoading={statusesLoading || listStatusesLoading}
          hasStatuses={!!workflowStatuses && workflowStatuses.length > 0}
          reorderChosenStatuses={reorderChosenStatuses}
        />
        <ListDialogFooter
          mode={mode}
          isValid={!!isValid}
          hasChanges={hasChanges}
          isSubmitting={isSubmitting}
          onClose={onClose}
        />
      </form>
    </div>
  );
};
